import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Space, Alert, Spin, Select, DatePicker, Table, Tag } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { baseUrl_SPB } from '../../../BaseUrl';
import axios from 'axios';
import dayjs from 'dayjs';

const PREFERENCE_TYPES = [
  { label: 'No preference', value: 'Nopreference' },
  { label: 'Ongoing', value: 'Ongoing' },
];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const WorkPreferencesPanel = ({ employeeId, token, onUpdate }) => {
  const [preferences, setPreferences] = useState({
    preferenceType: 'Nopreference',
    availability: [],
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingDayHours, setEditingDayHours] = useState(null);
  const [dayStartTime, setDayStartTime] = useState(null);
  const [dayEndTime, setDayEndTime] = useState(null);

  useEffect(() => {
    fetchPreferences();
  }, [employeeId]);

  const fetchPreferences = async () => {
    if (!token || !employeeId) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${baseUrl_SPB}/api/v1/employee/workpreferences?staffId=${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setPreferences(data || {
          preferenceType: 'Nopreference',
          availability: generateDefaultAvailability(),
        });
      } else {
        setPreferences({
          preferenceType: 'Nopreference',
          availability: generateDefaultAvailability(),
        });
      }
    } catch (err) {
      console.error('Preferences unavailable');
      setPreferences({
        preferenceType: 'Nopreference',
        availability: generateDefaultAvailability(),
      });
    } finally {
      setLoading(false);
    }
  };

  const generateDefaultAvailability = () => {
    const today = dayjs();
    const nextSunday = today.day(0);
    
    return DAYS.map((day, index) => ({
      date: nextSunday.add(index, 'day').format('YYYY-MM-DD'),
      day,
      starttime: '08:00',
      endtime: '17:00',
    }));
  };

  const handlePreferenceTypeChange = (value) => {
    setPreferences({
      ...preferences,
      preferenceType: value,
      availability: value === 'Ongoing' ? generateDefaultAvailability() : [],
    });
  };

  const handleDayTimeUpdate = (dayIndex, startTime, endTime) => {
    const updated = [...(preferences.availability || [])];
    updated[dayIndex] = {
      ...updated[dayIndex],
      starttime: startTime?.format('HH:mm') || updated[dayIndex].starttime,
      endtime: endTime?.format('HH:mm') || updated[dayIndex].endtime,
    };
    setPreferences({ ...preferences, availability: updated });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.post(
        `${baseUrl_SPB}/api/v1/employee/workpreferences/save`,
        {
          staffId: employeeId,
          ...preferences,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      onUpdate?.();
    } catch (err) {
      setError('Failed to save preferences.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const availabilityColumns = [
    {
      title: 'Day',
      dataIndex: 'day',
      key: 'day',
      width: 100,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
    },
    {
      title: 'Start Time',
      dataIndex: 'starttime',
      key: 'starttime',
      render: (time, _, index) => (
        <DatePicker
          picker="time"
          format="HH:mm"
          value={dayjs(`2000-01-01 ${time}`)}
          onChange={(val) => handleDayTimeUpdate(index, val, null)}
          size="small"
        />
      ),
    },
    {
      title: 'End Time',
      dataIndex: 'endtime',
      key: 'endtime',
      render: (time, _, index) => (
        <DatePicker
          picker="time"
          format="HH:mm"
          value={dayjs(`2000-01-01 ${time}`)}
          onChange={(val) => handleDayTimeUpdate(index, null, val)}
          size="small"
        />
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} />}
      
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        <Card title="Preference type">
          <Select
            value={preferences.preferenceType}
            onChange={handlePreferenceTypeChange}
            options={PREFERENCE_TYPES}
            style={{ width: '100%' }}
          />
        </Card>

        {preferences.preferenceType === 'Ongoing' && (
          <Card title="Weekly availability">
            <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
              Set your preferred start and end times for each day of the week
            </p>
            <Table
              rowKey="day"
              dataSource={preferences.availability || []}
              columns={availabilityColumns}
              pagination={false}
              size="small"
              scroll={{ x: 400 }}
            />
          </Card>
        )}

        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
          block
        >
          Save preferences
        </Button>
      </Space>
    </Spin>
  );
};

export default WorkPreferencesPanel;
